import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import { TablePagination } from '@/components/table-components';
import LandingLayout from '@/layouts/landing-layout';
import { Link, router } from '@inertiajs/react';
import {
    ArrowRight,
    Building2,
    CheckCircle2,
    ChevronRight,
    Clock,
    Compass,
    GraduationCap,
    Search,
    Sparkles,
    Users,
    X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

type Career = {
    id: number;
    category: 'Teaching' | 'Non-Teaching';
    positions_available?: number | null;
    title: string;
    description?: string | null;
    qualifications?: string[] | null;
    created_at?: string | null;
    updated_at?: string | null;
    link?: string | null;
};

type Pagination = {
    current_page: number;
    per_page: number;
    total: number;
};

type Filters = {
    category: 'Teaching' | 'Non-Teaching' | null;
    search?: string | null;
};

type Props = {
    careers: string;
    pagination: string;
    filters: string;
};

export default function LandingCareer({ careers: encodedCareers, pagination: encodedPagination, filters: encodedFilters }: Props) {
    const careers = useMemo<Career[]>(() => {
        try {
            return JSON.parse(atob(encodedCareers));
        } catch {
            return [];
        }
    }, [encodedCareers]);

    const pagination = useMemo<Pagination>(() => {
        try {
            return JSON.parse(atob(encodedPagination));
        } catch {
            return { current_page: 1, per_page: 6, total: 0 };
        }
    }, [encodedPagination]);

    const filters = useMemo<Filters>(() => {
        try {
            return JSON.parse(atob(encodedFilters));
        } catch {
            return { category: null, search: '' };
        }
    }, [encodedFilters]);

    const [searchText, setSearchText] = useState(filters.search || '');

    const lastPage = pagination.per_page > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;

    const from = pagination.total === 0 ? 0 : (pagination.current_page - 1) * pagination.per_page + 1;

    const to = pagination.total === 0 ? 0 : Math.min(pagination.total, pagination.current_page * pagination.per_page);

    const handlePageChange = (newPage: number) => {
        router.get(
            route('careers'),
            {
                category: filters.category,
                search: searchText,
                page: newPage,
            },
            {
                preserveState: true,
                preserveScroll: true,
            },
        );
    };

    const handleSelectCategory = (category: Filters['category']) => {
        router.get(
            route('careers'),
            {
                category,
                search: searchText,
                page: 1,
            },
            {
                preserveState: true,
                preserveScroll: true,
            },
        );
    };

    const handleClearSearch = () => {
        setSearchText('');
        router.get(
            route('careers'),
            {
                category: filters.category,
                search: '',
                page: 1,
            },
            {
                preserveState: true,
                preserveScroll: true,
            },
        );
    };

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
    };

    useEffect(() => {
        if (searchText === (filters.search || '')) {
            return;
        }

        const timeout = window.setTimeout(() => {
            router.get(
                route('careers'),
                {
                    category: filters.category,
                    search: searchText,
                    page: 1,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    replace: true,
                },
            );
        }, 400);

        return () => window.clearTimeout(timeout);
    }, [filters.category, filters.search, searchText]);

    const handleReadMoreClick = (careerId: number) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        if (!csrfToken) {
            return;
        }

        void fetch(route('careers.track-read-more-click', { career: careerId }), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            keepalive: true,
        });
    };

    return (
        <LandingLayout>
            <SeoHead
                title="Careers"
                description="Browse open career opportunities and job postings at Northeastern College."
                image={`${baseUrl}/images/hero/career.png`}
            />

            <section id="careers" className="bg-muted pb-16">
                {/* Hero Banner Section */}
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/career.png"
                        alt="Northeastern College campus community"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />

                     
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.3),transparent_60%)]" />

                     
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                     
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-emerald-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Careers</span>
                        </div>

                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Career Opportunities
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Join a progressive community committed to nurturing learners, supporting excellent service, and building meaningful
                            careers across teaching and non-teaching roles.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    {/* Filter & Search Bar */}
                    <div className="bg-card border-border flex flex-col items-center justify-between gap-6 rounded-2xl border p-4 shadow-sm lg:flex-row lg:p-6">
                        {/* Tab Pill Selector */}
                        <div className="flex flex-wrap gap-2 w-full lg:w-auto lg:shrink-0 lg:flex-nowrap">
                            <button
                                type="button"
                                onClick={() => handleSelectCategory(null)}
                                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold transition md:text-sm ${
                                    filters.category === null
                                        ? 'bg-[#0A7A35] text-white shadow-sm'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                            >
                                <Compass className="size-4" />
                                All Opportunities
                            </button>
                            {(['Teaching', 'Non-Teaching'] as const).map((category) => (
                                <button
                                    key={category}
                                    type="button"
                                    onClick={() => handleSelectCategory(category)}
                                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold transition md:text-sm ${
                                        filters.category === category
                                            ? 'bg-[#0A7A35] text-white shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    {category === 'Teaching' ? <GraduationCap className="size-4" /> : <Building2 className="size-4" />}
                                    {category}
                                </button>
                            ))}
                        </div>

                        {/* Search Input */}
                        <div className="flex w-full items-center gap-2 lg:max-w-md">
                            <div className="relative w-full">
                                <Search className="text-muted-foreground absolute top-1/2 left-4 size-4 -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Search job titles or keywords..."
                                    className="border-border bg-card text-foreground w-full rounded-full border py-2.5 pr-10 pl-11 text-sm shadow-inner transition-all duration-200 focus:border-[#0A7A35] focus:ring-2 focus:ring-[#0A7A35]/30 focus:outline-none"
                                />
                                {searchText && (
                                    <button
                                        type="button"
                                        onClick={handleClearSearch}
                                        className="hover:bg-muted text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 rounded-full p-0.5 transition-colors"
                                    >
                                        <X className="size-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Careers Listing / Grid */}
                    {careers.length === 0 ? (
                        <div className="bg-card border-border mt-8 flex flex-col items-center justify-center rounded-2xl border px-4 py-20 text-center shadow-sm">
                            <div className="bg-muted text-muted-foreground mb-4 rounded-full p-4">
                                <Search className="size-10" />
                            </div>
                            <h3 className="text-foreground mb-1 text-lg font-bold">No vacancies found</h3>
                            <p className="text-muted-foreground max-w-md text-sm">
                                {searchText
                                    ? `We couldn't find any opportunities matching "${searchText}". Try modifying your search or filters.`
                                    : 'There are no career opportunities available in this category at the moment.'}
                            </p>
                            {searchText && (
                                <button
                                    type="button"
                                    onClick={handleClearSearch}
                                    className="bg-muted hover:bg-muted/80 text-foreground border-border mt-6 inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors"
                                >
                                    Clear search query
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                                {careers.map((career) => (
                                    <article
                                        key={career.id}
                                        className="group border-border bg-card relative flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                                    >
                                        {/* Colored Accent Line */}

                                        {/* Card Header Category & Last Updated */}
                                        <div className="mb-4 flex items-center justify-between">
                                            <span
                                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                                                    career.category === 'Teaching'
                                                        ? 'bg-[#0A7A35]/10 text-[#0A7A35]'
                                                        : 'bg-muted text-muted-foreground border-border/40 border'
                                                }`}
                                            >
                                                {career.category === 'Teaching' ? (
                                                    <GraduationCap className="size-3.5" />
                                                ) : (
                                                    <Building2 className="size-3.5" />
                                                )}
                                                {career.category}
                                            </span>

                                            <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                                                <Clock className="text-muted-foreground/80 size-3.5" />
                                                {formatDate(career.updated_at ?? career.created_at)}
                                            </span>
                                        </div>

                                        {/* Card Job Title */}
                                        <h4 className="text-foreground line-clamp-2 text-xl leading-snug font-bold transition-colors group-hover:text-[#0A7A35]">
                                            {career.title}
                                        </h4>

                                        {/* Metadata */}
                                        <div className="border-border/50 mt-4 border-b pb-4">
                                            <div className="text-muted-foreground flex items-center gap-2 text-xs">
                                                <Users className="size-4 shrink-0 text-[#0A7A35]" />
                                                <span>
                                                    {(career.positions_available ?? 1) === 1
                                                        ? '1 Open Role'
                                                        : `${career.positions_available ?? 1} Open Roles`}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Job Brief Description */}
                                        {career.description && (
                                            <p className="text-muted-foreground mt-4 line-clamp-3 text-sm leading-relaxed">{career.description}</p>
                                        )}

                                        {/* Key Qualifications Preview */}
                                        {!!career.qualifications?.length && (
                                            <div className="mt-5 space-y-2.5">
                                                <h5 className="text-muted-foreground flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase">
                                                    <Sparkles className="size-3.5 text-[#0A7A35]" />
                                                    Requirements
                                                </h5>
                                                <ul className="space-y-2">
                                                    {career.qualifications.slice(0, 3).map((qualification, index) => (
                                                        <li
                                                            key={`${career.id}-qualification-${index}`}
                                                            className="text-muted-foreground flex items-start gap-2 text-xs leading-normal"
                                                        >
                                                            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-[#0A7A35]" />
                                                            <span className="line-clamp-2">{qualification}</span>
                                                        </li>
                                                    ))}
                                                    {career.qualifications.length > 3 && (
                                                        <li className="pl-6 text-[11px] font-semibold text-[#0A7A35]">
                                                            + {career.qualifications.length - 3} more requirements
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Action / Apply CTA */}
                                        {career.link && (
                                            <div className="mt-auto flex justify-end pt-6">
                                                <a
                                                    href={career.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => handleReadMoreClick(career.id)}
                                                    className="inline-flex items-center gap-2 rounded-full bg-[#0A7A35]/10 px-5 py-2.5 text-sm font-semibold text-[#0A7A35] transition-all duration-300 group-hover:bg-[#0A7A35] group-hover:text-white group-hover:shadow-md"
                                                >
                                                    Apply Now
                                                    <ArrowRight
                                                        className="size-4 transition-transform duration-300 group-hover:translate-x-1"
                                                        aria-hidden="true"
                                                    />
                                                </a>
                                            </div>
                                        )}
                                    </article>
                                ))}
                            </div>

                            {/* Pagination component */}
                            <div className="mt-8">
                                <TablePagination
                                    hidePerPage
                                    currentPage={pagination.current_page}
                                    lastPage={lastPage}
                                    perPage={pagination.per_page}
                                    total={pagination.total}
                                    from={from}
                                    to={to}
                                    onPageChange={handlePageChange}
                                    onPerPageChange={() => {}}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
